import { actions } from './reducer';
import { api } from '../../api';
import { fetchUser as fetchUserProfile } from '../profile/effects';
import * as notifications from '../notification/effects';
import { getCurrency } from 'planoplan-ui-kit/core/helpers/get-currency';
import i18next from 'i18next';
import { formatKbytes } from '@lib/format-kb';
import { textHooks } from '../../lib/text-hooks';
import languages from './languages';
import includes from 'array-includes';

const delayBetweenCalls = 0;

export const apiCall = (func) => async (dispatch, getState) => {
  const { fetching } = getState().common;

  if (fetching.status) {
    dispatch(actions.fetching.end());
  }

  dispatch(actions.fetching.start());
  let result;

  try {
    result = await func();
  } catch (e) {
    dispatch(actions.fetching.fail());
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetch.error'),
      })
    );
    dispatch(setProcessing(false));
  }

  dispatch(actions.fetching.end());

  return result;
};

export const fetchUser = () => async (dispatch, getState) => {
  const userData = window._user;
  const lastUpdate = getState().common.userUpdate;
  const initiated = getState().common.initiated;

  if (initiated) return;

  dispatch(actions.setInit('start'));

  if (userData && lastUpdate === 0) {
    dispatch(actions.setUserData(userData));
    dispatch(actions.setLastUpdate('userUpdate', -Date.now()));
  }
  dispatch(fetchUserProfile());
  dispatch(fetchGoods());
  dispatch(fetchTeams(dispatch(actions.setInit('end'))));
};

export const fetchGoods = (value) => async (dispatch, getState) => {
  const lastUpdate = getState().common.goodsUpdate;
  if (Date.now() - lastUpdate < delayBetweenCalls) return;
  dispatch(actions.setLastUpdate('goodsUpdate'));

  const currentTeam = value || getState().common.currentTeam;
  const response = await dispatch(apiCall(() => api.getGoods(currentTeam)));

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetch.error'),
      })
    );

    return;
  }

  // Для команды закончился тариф
  if (!response.data.success && response.data.errorText === 'Time expired') {
    const { data } = response.data;

    const goods = {
      goods: [],
      currency: getCurrency(data.currency),
      tariff: {
        expire: -1,
        alias: data.tariff_alias,
        licenseCount: data.license_count,
        diskspaceGoodsId: data.diskspace_goods_id,
        name: i18next.t('cabinet.misc.blocked'),
      },
    };

    dispatch(actions.setGoods(goods));
  }

  if (response.data.success) {
    const { data } = response.data;

    const setParamsGoods = (goods) => {
      const aliases = ['render', 'panorama'];

      goods.forEach((item) => {
        // Парсит имена товаров, которые соответсвуют алиасам
        if (includes(aliases, item.alias)) {
          const text = textHooks(item.caption);

          item.view_name = text.out_hooks;
          item.view_size = text.in_hooks;
        }

        // Добавляет флаг, если товар не доступен пользователю
        if (item.price === null && item.in_sets.length < 1) {
          item.view_disabled = true;
        }
      });

      return goods;
    };

    const goods = {
      currency: getCurrency(data.currency),
      goods: setParamsGoods(data.goods) || [],
      tariff: {
        expire: data.tariff && data.tariff.expire,
        name: data.tariff && data.tariff.name,
        alias: data.tariff.alias,
        options: {
          createFolders: data.tariff && data.tariff.options['editor.createFolders'] === 'true',
          allowMultiwidget: data.tariff && data.tariff.options['editor.allowMultiwidget'] === 'true',
        },
        diskspaceGoodsId: data.tariff.diskspace_goods_id || 0,
      },
      storeUrl: data.storeUrl,
    };

    if (window.uiInfoPoints) {
      window.uiInfoPoints.createPoints({ tariff: data.tariff.alias, team_id: currentTeam });
      window.uiInfoPoints.on('ON_CHANGE_USER');
    }

    dispatch(actions.setGoods(goods));
  }
};

export const fetchPricing = () => async (dispatch, getState) => {
  const currentTeam = getState().common.currentTeam;
  const response = await dispatch(apiCall(() => api.getPricing(currentTeam)));

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetch.error'),
      })
    );

    return;
  }

  if (response.data.success) {
    const { data } = response.data;
    const products = [];

    data.forEach((tariff) => {
      tariff.goods.forEach((item) => {
        const is = products.some((product) => product.id === item.id);
        if (!is) {
          products.push(item);
        }
      });
    });

    dispatch(actions.setProducts(products));
    dispatch(actions.setAllPricing(data));
  }
};

export const fetchTeams = () => async (dispatch, getState) => {
  const lastUpdate = getState().common.teamsUpdate;
  if (Date.now() - lastUpdate < delayBetweenCalls) return;
  dispatch(actions.setLastUpdate('teamsUpdate'));

  const response = await dispatch(apiCall(() => api.getTeams()));

  if (!response || response.status !== 200 || !response.data.success) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetch.error'),
      })
    );

    return;
  }

  if (response.data.success) {
    const { data } = response.data;
    let teams = {};

    if (data.teams) {
      teams = data.teams.map((item) => {
        const disk_usage = item.disk_usage;
        const freeSpace = disk_usage.quota_limit - disk_usage.quota_usage;
        const quotaFreePercent = (disk_usage.quota_usage / disk_usage.quota_limit) * 100;

        return {
          blockMembers: item.block_members,
          caption: item.caption,
          created: item.created,
          id: item.id,
          licenseCount: item.license_count,
          licenseExpire: item.license_expire,
          members: item.members,
          modified: item.modified,
          tariffsId: item.tariffs_id,
          usersId: item.users_id,
          email: item.email,
          about: item.about,
          site: item.site,
          socialLinks: item.social_links,
          avatar: item.logo,
          hasQuota: !!disk_usage,
          quotaLimit: formatKbytes(disk_usage.quota_limit),
          quotaUsage: formatKbytes(disk_usage.quota_usage),
          quotaFree: freeSpace > 0 ? formatKbytes(freeSpace) : 0,
          quotaFreePercent: quotaFreePercent < 100 ? quotaFreePercent : 100,
        };
      });

      dispatch(actions.setTeams({ teams: teams }));
    }
  }
};

export const setCurrentTeam = (value, NOUPDATE, callback) => (dispatch, getState) => {
  dispatch(actions.setCurrentTeam(value));
  const state = getState();
  if (!NOUPDATE) {
    dispatch(fetchGoods(value));
  }

  const team = state.common.teams.find((item) => item.id === value) || {};

  if (!NOUPDATE && callback) {
    callback({
      currentTeam: value,
      licenseCount: team.licenseCount,
      licenseExpire: team.licenseExpire,
      avatar: team ? team.avatar : state.common.avatar,
    });
  }
};

export const fetchCards = () => async (dispatch) => {
  const response = await dispatch(apiCall(() => api.getCards()));

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetchCards.error'),
      })
    );

    return;
  }

  const cards = response.data.data.items.filter((item) => item.type === 'bank_card' || item.type === 'card');

  dispatch(actions.setCards(cards));
};

export const fetchPaymentMethods = () => async (dispatch) => {
  const response = await dispatch(apiCall(() => api.getPaymentMethods()));

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetchCards.error'),
      })
    );

    return;
  }

  dispatch(actions.setPaymentMethods(response.data.data));
};

export const fetchCountries = () => async (dispatch) => {
  const response = await dispatch(apiCall(() => api.getCountries()));

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.fetchCards.error'),
      })
    );

    return;
  }

  dispatch(actions.setCountries(response.data.data.countries));
};

export const fetchAutoPayStatus = () => async (dispatch, getState) => {
  const team_id = getState().common.currentTeam;

  const response = await dispatch(apiCall(() => api.getAutoPay(team_id)));
  const status = response.data.success && Object.values(response.data.data).length > 0;

  dispatch(actions.setAutoPayStatus(status));
};

export const cancelAutoPay = () => async (dispatch, getState) => {
  const team_id = getState().common.currentTeam;

  const response = await dispatch(apiCall(() => api.cancelAutoPay(team_id)));
  const status = response.data.success && Object.values(response.data.data).length > 0;

  if (!response || response.status !== 200) {
    dispatch(
      notifications.showNotification({
        type: 'ERROR',
        message: i18next.t('cabinet.notification.autopayment.error'),
      })
    );

    return;
  }

  dispatch(
    notifications.showNotification({
      type: 'SUCCESS',
      message: i18next.t('cabinet.notification.autopayment.success'),
    })
  );

  dispatch(actions.setAutoPayStatus(status));
};

export const setLocale = () => (dispatch) => {
  const url = window.location.href;
  const languages = ['en', 'ru', 'de', 'fi', 'fr', 'cn', 'jp', 'pt', 'th', 'es', 'tr', 'br'];
  const parseLang = url.match(/\/([\w]{2})\//g);
  let lang = '';

  if (parseLang) {
    const result = parseLang[0].replace(/\//g, '');
    const isSupport = languages.indexOf(result) + 1;

    isSupport ? (lang = result) : (lang = '');
  } else {
    lang = 'ru';
  }

  dispatch(actions.setLocale(lang));
};

export const setProcessing = (status) => (dispatch) => {
  dispatch(actions.setProcessing(status));
};

export const setLanguages = () => (dispatch) => {
  const getAvailables = () => {
    if (window.location.href.indexOf('cabinet') !== -1) {
      return [languages.en, languages.ru];
    }
    if (window.location.href.indexOf('pricing') !== -1) {
      return [languages.en, languages.ru];
    } else {
      return [languages.en, languages.ru, languages.de, languages.fi, languages.br];
    }
  };

  const list = getAvailables();
  const current = list.find((item) => item.lang === window._lang) || languages.en;

  dispatch(
    actions.setLanguages({
      current,
      list,
    })
  );
};

export const changeLanguage = (lang) => () => {
  const path = window.location.pathname.replace(/\/([\w]{2})\//g, '');
  // После удаления /ru/ из урла, первый '/' пропадает
  const pathNormalize = path[0] === '/' ? path : '/' + path;

  window.location = window.location.origin + '/' + lang + pathNormalize;
};

export const logout = () => {
  window.location = '/logout';
};
